import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Search,
  Download,
  Edit2,
  Trash2,
  Key,
  Users,
  MoreVertical,
} from 'lucide-react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';

import { apiClient } from '../../lib/api';
import { cn, downloadCSV, formatDateTime } from '../../lib/utils';
import { useAuth } from '../../providers/AuthProvider';
import { useToast } from '../../components/ui/Toast';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card, CardContent } from '../../components/ui/Card';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../components/ui/Table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/Dialog';
import Checkbox from '../../components/ui/Checkbox';
import Label from '../../components/ui/Label';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

import type { User, Role, CreateUserData, UpdateUserData, ChangePasswordData } from '../../types/auth';

interface UserFormData {
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  role: string;
  selectedRoles: string[];
  isActive: boolean;
}

const initialFormData: UserFormData = {
  username: '',
  email: '',
  password: '',
  confirmPassword: '',
  firstName: '',
  lastName: '',
  role: 'user',
  selectedRoles: [],
  isActive: true,
};

interface PasswordFormData {
  newPassword: string;
  confirmPassword: string;
}

const initialPasswordData: PasswordFormData = {
  newPassword: '',
  confirmPassword: '',
};

export default function UsersPage() {
  const { t, i18n } = useTranslation('users');
  const { hasPermission, user: currentUser } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<UserFormData>(initialFormData);
  const [passwordData, setPasswordData] = useState<PasswordFormData>(initialPasswordData);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const isRtl = i18n.dir() === 'rtl';

  // Fetch users
  const {
    data: usersData,
    isLoading: isLoadingUsers,
    error: usersError,
  } = useQuery({
    queryKey: ['users'],
    queryFn: () => apiClient.get<{ data: User[] }>('/users'),
  });

  // Fetch roles
  const { data: rolesData } = useQuery({
    queryKey: ['roles'],
    queryFn: () => apiClient.get<{ data: Role[] }>('/roles'),
  });

  const users = usersData?.data ?? [];
  const roles = rolesData?.data ?? [];

  // Filtered users based on search
  const filteredUsers = useMemo(() => {
    if (!searchTerm) return users;

    const lowerSearch = searchTerm.toLowerCase();
    return users.filter(
      (user) =>
        user.username.toLowerCase().includes(lowerSearch) ||
        user.email?.toLowerCase().includes(lowerSearch) ||
        user.firstName?.toLowerCase().includes(lowerSearch) ||
        user.lastName?.toLowerCase().includes(lowerSearch) ||
        user.displayName?.toLowerCase().includes(lowerSearch)
    );
  }, [users, searchTerm]);

  // Mutations
  const createUserMutation = useMutation({
    mutationFn: (data: CreateUserData) => apiClient.post<User>('/users', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success(t('messages.createSuccess'));
      handleCloseCreateDialog();
    },
    onError: (error: Error) => {
      toast.error(t('common:error'), error.message);
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: UpdateUserData }) =>
      apiClient.put<User>(`/users/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success(t('messages.updateSuccess'));
      handleCloseEditDialog();
    },
    onError: (error: Error) => {
      toast.error(t('common:error'), error.message);
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: (id: string | number) => apiClient.delete(`/users/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success(t('messages.deleteSuccess'));
      handleCloseDeleteDialog();
    },
    onError: (error: Error) => {
      toast.error(t('common:error'), error.message);
    },
  });

  const changePasswordMutation = useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: ChangePasswordData }) =>
      apiClient.post(`/users/${id}/change-password`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success(t('messages.passwordChangeSuccess'));
      handleClosePasswordDialog();
    },
    onError: (error: Error) => {
      toast.error(t('common:error'), error.message);
    },
  });

  // Form validation
  const validateForm = useCallback(
    (data: UserFormData, isEdit: boolean): Record<string, string> => {
      const errors: Record<string, string> = {};

      if (!data.username.trim()) {
        errors.username = t('validation.usernameRequired');
      } else if (data.username.length < 3) {
        errors.username = t('validation.usernameMinLength');
      }

      if (!data.email.trim()) {
        errors.email = t('validation.emailRequired');
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
        errors.email = t('validation.emailInvalid');
      }

      if (!isEdit) {
        if (!data.password) {
          errors.password = t('validation.passwordRequired');
        } else if (data.password.length < 8) {
          errors.password = t('validation.passwordMinLength');
        }

        if (data.password !== data.confirmPassword) {
          errors.confirmPassword = t('validation.passwordMismatch');
        }
      }

      if (!data.firstName.trim()) {
        errors.firstName = t('validation.firstNameRequired');
      }

      if (!data.lastName.trim()) {
        errors.lastName = t('validation.lastNameRequired');
      }

      return errors;
    },
    [t]
  );

  const validatePasswordForm = useCallback(
    (data: PasswordFormData): Record<string, string> => {
      const errors: Record<string, string> = {};

      if (!data.newPassword) {
        errors.newPassword = t('validation.passwordRequired');
      } else if (data.newPassword.length < 8) {
        errors.newPassword = t('validation.passwordMinLength');
      }

      if (data.newPassword !== data.confirmPassword) {
        errors.confirmPassword = t('validation.passwordMismatch');
      }

      return errors;
    },
    [t]
  );

  // Handlers
  const handleCloseCreateDialog = () => {
    setIsCreateDialogOpen(false);
    setFormData(initialFormData);
    setFormErrors({});
  };

  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
    setSelectedUser(null);
    setFormData(initialFormData);
    setFormErrors({});
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setSelectedUser(null);
  };

  const handleClosePasswordDialog = () => {
    setIsPasswordDialogOpen(false);
    setSelectedUser(null);
    setPasswordData(initialPasswordData);
    setFormErrors({});
  };

  const handleOpenEditDialog = (user: User) => {
    setSelectedUser(user);
    setFormData({
      username: user.username,
      email: user.email ?? '',
      password: '',
      confirmPassword: '',
      firstName: user.firstName ?? user.displayName?.split(' ')[0] ?? '',
      lastName: user.lastName ?? user.displayName?.split(' ').slice(1).join(' ') ?? '',
      role: user.role,
      selectedRoles: user.roles?.map((r) => r._id) ?? [],
      isActive: user.isActive ?? true,
    });
    setIsEditDialogOpen(true);
  };

  const handleOpenDeleteDialog = (user: User) => {
    if (currentUser?.id === user.id) {
      toast.error(t('messages.cannotDeleteSelf'));
      return;
    }
    setSelectedUser(user);
    setIsDeleteDialogOpen(true);
  };

  const handleOpenPasswordDialog = (user: User) => {
    setSelectedUser(user);
    setPasswordData(initialPasswordData);
    setIsPasswordDialogOpen(true);
  };

  const handleCreateUser = () => {
    const errors = validateForm(formData, false);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    createUserMutation.mutate({
      username: formData.username,
      email: formData.email,
      password: formData.password,
      firstName: formData.firstName,
      lastName: formData.lastName,
      role: formData.role as User['role'],
      roles: formData.selectedRoles,
      isActive: formData.isActive,
    });
  };

  const handleUpdateUser = () => {
    if (!selectedUser) return;

    const errors = validateForm(formData, true);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    updateUserMutation.mutate({
      id: selectedUser._id || selectedUser.id,
      data: {
        username: formData.username,
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: formData.role as User['role'],
        roles: formData.selectedRoles,
        isActive: formData.isActive,
      },
    });
  };

  const handleDeleteUser = () => {
    if (!selectedUser) return;
    deleteUserMutation.mutate(selectedUser._id || selectedUser.id);
  };

  const handleChangePassword = () => {
    if (!selectedUser) return;

    const errors = validatePasswordForm(passwordData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    changePasswordMutation.mutate({
      id: selectedUser._id || selectedUser.id,
      data: {
        newPassword: passwordData.newPassword,
        confirmPassword: passwordData.confirmPassword,
      },
    });
  };

  const handleExportCSV = () => {
    const exportData = filteredUsers.map((user) => ({
      [t('fields.id')]: user.id,
      [t('fields.username')]: user.username,
      [t('fields.email')]: user.email ?? '',
      [t('fields.firstName')]: user.firstName ?? user.displayName?.split(' ')[0] ?? '',
      [t('fields.lastName')]: user.lastName ?? user.displayName?.split(' ').slice(1).join(' ') ?? '',
      [t('fields.role')]: user.role,
      [t('fields.status')]: (user.isActive ?? true) ? t('common:active') : t('common:inactive'),
      [t('fields.createdAt')]: user.createdAt
        ? formatDateTime(user.createdAt, i18n.language === 'ar' ? 'ar-EG' : 'en-US')
        : '',
    }));

    downloadCSV(exportData, `users_${new Date().toISOString().split('T')[0]}`);
  };

  const handleRoleToggle = (roleId: string) => {
    setFormData((prev) => ({
      ...prev,
      selectedRoles: prev.selectedRoles.includes(roleId)
        ? prev.selectedRoles.filter((id) => id !== roleId)
        : [...prev.selectedRoles, roleId],
    }));
  };

  const getRoleDisplayName = (role: Role): string => {
    return i18n.language === 'ar' ? role.displayNameAr : role.displayNameEn;
  };

  // Check if error is a service unavailable error (503/404) - show empty state instead
  const isServiceUnavailable = usersError && (
    (usersError as any)?.response?.status === 503 ||
    (usersError as any)?.response?.status === 404 ||
    (usersError as any)?.message?.includes('Service temporarily unavailable') ||
    (usersError as any)?.message?.includes('ECONNREFUSED')
  );

  // Loading state
  if (isLoadingUsers && !isServiceUnavailable) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" role="status" aria-live="polite">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Error state (only show if not service unavailable - those will show empty state)
  if (usersError && !isServiceUnavailable) {
    const errorMessage = usersError instanceof Error ? usersError.message : t('errors.generic', 'An unexpected error occurred');
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center" role="alert">
        <Users className="h-12 w-12 text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
          {t('common.error', 'Error')}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {errorMessage}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        actions={
          hasPermission('users:write') && (
            <Button
              onClick={() => setIsCreateDialogOpen(true)}
              leftIcon={<Plus className="h-4 w-4" />}
            >
              {t('addUser')}
            </Button>
          )
        }
      />

      <Card>
        <CardContent className="p-0">
          {/* Search and Actions Bar */}
          <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
              <div className="relative flex-1 max-w-md">
                <Input
                  type="text"
                  placeholder={t('searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  leftIcon={<Search className="h-4 w-4" />}
                />
              </div>
              <Button
                variant="outline"
                onClick={handleExportCSV}
                leftIcon={<Download className="h-4 w-4" />}
              >
                {t('exportCSV')}
              </Button>
            </div>
          </div>

          {/* Users Table */}
          {filteredUsers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Users className="h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                {t('noUsers')}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-sm">
                {t('noUsersDescription')}
              </p>
              {hasPermission('users:write') && (
                <Button
                  className="mt-4"
                  onClick={() => setIsCreateDialogOpen(true)}
                  leftIcon={<Plus className="h-4 w-4" />}
                >
                  {t('addUser')}
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('fields.username')}</TableHead>
                  <TableHead>{t('fields.fullName')}</TableHead>
                  <TableHead>{t('fields.email')}</TableHead>
                  <TableHead>{t('fields.roles')}</TableHead>
                  <TableHead>{t('fields.status')}</TableHead>
                  <TableHead className="ltr:text-right rtl:text-left">
                    {t('common:actions')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user._id || user.id}>
                    <TableCell className="font-medium">{user.username}</TableCell>
                    <TableCell>{`${user.firstName} ${user.lastName}`}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {user.roles && user.roles.length > 0 ? (
                          user.roles.map((role) => (
                            <span
                              key={role._id}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200"
                            >
                              {getRoleDisplayName(role)}
                            </span>
                          ))
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                            {t(`roles.${user.role}`)}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span
                        className={cn(
                          'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                          user.isActive
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        )}
                      >
                        {user.isActive ? t('common:active') : t('common:inactive')}
                      </span>
                    </TableCell>
                    <TableCell className="ltr:text-right rtl:text-left">
                      <Menu as="div" className="relative inline-block text-left">
                        <MenuButton
                          as={Button}
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                        >
                          <span className="sr-only">{t('common:actions')}</span>
                          <MoreVertical className="h-4 w-4" />
                        </MenuButton>
                        <MenuItems
                          className={cn(
                            'absolute z-10 mt-2 w-48 rounded-md bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none',
                            isRtl ? 'left-0' : 'right-0'
                          )}
                        >
                          <div className="py-1">
                            {hasPermission('users:write') && (
                              <MenuItem>
                                {({ focus }) => (
                                  <button
                                    onClick={() => handleOpenEditDialog(user)}
                                    className={cn(
                                      'w-full flex items-center gap-2 px-4 py-2 text-sm',
                                      focus
                                        ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                                        : 'text-gray-700 dark:text-gray-300'
                                    )}
                                  >
                                    <Edit2 className="h-4 w-4" />
                                    {t('editUser')}
                                  </button>
                                )}
                              </MenuItem>
                            )}
                            {hasPermission('users:write') && (
                              <MenuItem>
                                {({ focus }) => (
                                  <button
                                    onClick={() => handleOpenPasswordDialog(user)}
                                    className={cn(
                                      'w-full flex items-center gap-2 px-4 py-2 text-sm',
                                      focus
                                        ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                                        : 'text-gray-700 dark:text-gray-300'
                                    )}
                                  >
                                    <Key className="h-4 w-4" />
                                    {t('changePassword')}
                                  </button>
                                )}
                              </MenuItem>
                            )}
                            {hasPermission('users:delete') && (
                              <MenuItem>
                                {({ focus }) => (
                                  <button
                                    onClick={() => handleOpenDeleteDialog(user)}
                                    className={cn(
                                      'w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 dark:text-red-400',
                                      focus && 'bg-red-50 dark:bg-red-900/20'
                                    )}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                    {t('deleteUser')}
                                  </button>
                                )}
                              </MenuItem>
                            )}
                          </div>
                        </MenuItems>
                      </Menu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={isCreateDialogOpen} onClose={handleCloseCreateDialog}>
        <DialogHeader onClose={handleCloseCreateDialog}>
          <DialogTitle>{t('addUser')}</DialogTitle>
          <DialogDescription>{t('subtitle')}</DialogDescription>
        </DialogHeader>
        <DialogContent>
          <div className="space-y-4">
            <Input
              label={t('fields.username')}
              name="username"
              value={formData.username}
              onChange={(e) => setFormData((prev) => ({ ...prev, username: e.target.value }))}
              error={formErrors.username}
              isRequired
            />
            <Input
              label={t('fields.email')}
              name="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
              error={formErrors.email}
              isRequired
            />
            <div className="grid grid-cols-2 gap-4">
              <Input
                label={t('fields.firstName')}
                name="firstName"
                value={formData.firstName}
                onChange={(e) => setFormData((prev) => ({ ...prev, firstName: e.target.value }))}
                error={formErrors.firstName}
                isRequired
              />
              <Input
                label={t('fields.lastName')}
                name="lastName"
                value={formData.lastName}
                onChange={(e) => setFormData((prev) => ({ ...prev, lastName: e.target.value }))}
                error={formErrors.lastName}
                isRequired
              />
            </div>
            <Input
              label={t('fields.password')}
              name="password"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
              error={formErrors.password}
              isRequired
            />
            <Input
              label={t('fields.confirmPassword')}
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, confirmPassword: e.target.value }))
              }
              error={formErrors.confirmPassword}
              isRequired
            />

            {/* Roles Selection */}
            {roles.length > 0 && (
              <div>
                <Label className="mb-2">{t('fields.roles')}</Label>
                <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md p-3">
                  {roles.map((role) => (
                    <Checkbox
                      key={role._id}
                      id={`role-${role._id}`}
                      label={getRoleDisplayName(role)}
                      checked={formData.selectedRoles.includes(role._id)}
                      onCheckedChange={() => handleRoleToggle(role._id)}
                    />
                  ))}
                </div>
              </div>
            )}

            <Checkbox
              id="isActive"
              label={t('fields.isActive')}
              checked={formData.isActive}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({ ...prev, isActive: checked }))
              }
            />
          </div>
        </DialogContent>
        <DialogFooter>
          <Button variant="outline" onClick={handleCloseCreateDialog}>
            {t('common:cancel')}
          </Button>
          <Button onClick={handleCreateUser} isLoading={createUserMutation.isPending}>
            {t('common:save')}
          </Button>
        </DialogFooter>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={isEditDialogOpen} onClose={handleCloseEditDialog}>
        <DialogHeader onClose={handleCloseEditDialog}>
          <DialogTitle>{t('editUser')}</DialogTitle>
          <DialogDescription>{t('subtitle')}</DialogDescription>
        </DialogHeader>
        <DialogContent>
          <div className="space-y-4">
            <Input
              label={t('fields.username')}
              name="username"
              value={formData.username}
              onChange={(e) => setFormData((prev) => ({ ...prev, username: e.target.value }))}
              error={formErrors.username}
              isRequired
            />
            <Input
              label={t('fields.email')}
              name="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
              error={formErrors.email}
              isRequired
            />
            <div className="grid grid-cols-2 gap-4">
              <Input
                label={t('fields.firstName')}
                name="firstName"
                value={formData.firstName}
                onChange={(e) => setFormData((prev) => ({ ...prev, firstName: e.target.value }))}
                error={formErrors.firstName}
                isRequired
              />
              <Input
                label={t('fields.lastName')}
                name="lastName"
                value={formData.lastName}
                onChange={(e) => setFormData((prev) => ({ ...prev, lastName: e.target.value }))}
                error={formErrors.lastName}
                isRequired
              />
            </div>

            {/* Roles Selection */}
            {roles.length > 0 && (
              <div>
                <Label className="mb-2">{t('fields.roles')}</Label>
                <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md p-3">
                  {roles.map((role) => (
                    <Checkbox
                      key={role._id}
                      id={`edit-role-${role._id}`}
                      label={getRoleDisplayName(role)}
                      checked={formData.selectedRoles.includes(role._id)}
                      onCheckedChange={() => handleRoleToggle(role._id)}
                    />
                  ))}
                </div>
              </div>
            )}

            <Checkbox
              id="edit-isActive"
              label={t('fields.isActive')}
              checked={formData.isActive}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({ ...prev, isActive: checked }))
              }
            />
          </div>
        </DialogContent>
        <DialogFooter>
          <Button variant="outline" onClick={handleCloseEditDialog}>
            {t('common:cancel')}
          </Button>
          <Button onClick={handleUpdateUser} isLoading={updateUserMutation.isPending}>
            {t('common:save')}
          </Button>
        </DialogFooter>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogHeader onClose={handleCloseDeleteDialog}>
          <DialogTitle>{t('deleteUser')}</DialogTitle>
          <DialogDescription>{t('messages.confirmDelete')}</DialogDescription>
        </DialogHeader>
        <DialogContent>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('messages.confirmDeleteDescription')}
          </p>
          {selectedUser && (
            <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-md">
              <p className="font-medium text-gray-900 dark:text-gray-100">
                {selectedUser.firstName} {selectedUser.lastName}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{selectedUser.email}</p>
            </div>
          )}
        </DialogContent>
        <DialogFooter>
          <Button variant="outline" onClick={handleCloseDeleteDialog}>
            {t('common:cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleDeleteUser}
            isLoading={deleteUserMutation.isPending}
          >
            {t('common:delete')}
          </Button>
        </DialogFooter>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={isPasswordDialogOpen} onClose={handleClosePasswordDialog}>
        <DialogHeader onClose={handleClosePasswordDialog}>
          <DialogTitle>{t('changePassword')}</DialogTitle>
          {selectedUser && (
            <DialogDescription>
              {selectedUser.firstName} {selectedUser.lastName}
            </DialogDescription>
          )}
        </DialogHeader>
        <DialogContent>
          <div className="space-y-4">
            <Input
              label={t('newPassword')}
              name="newPassword"
              type="password"
              value={passwordData.newPassword}
              onChange={(e) =>
                setPasswordData((prev) => ({ ...prev, newPassword: e.target.value }))
              }
              error={formErrors.newPassword}
              isRequired
            />
            <Input
              label={t('confirmNewPassword')}
              name="confirmPassword"
              type="password"
              value={passwordData.confirmPassword}
              onChange={(e) =>
                setPasswordData((prev) => ({ ...prev, confirmPassword: e.target.value }))
              }
              error={formErrors.confirmPassword}
              isRequired
            />
          </div>
        </DialogContent>
        <DialogFooter>
          <Button variant="outline" onClick={handleClosePasswordDialog}>
            {t('common:cancel')}
          </Button>
          <Button onClick={handleChangePassword} isLoading={changePasswordMutation.isPending}>
            {t('common:save')}
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}
